// RND-1/RND-6: image entities render on the bg layer (after the canvas grid, below all
// vectors), in layer/z order relative to other images. Adjustments via ctx.filter (IMG-6).
import type { Doc, ImageEntity, Layer } from "../doc/entities";
import type { RenderView } from "./renderEntities";
import { applyWorldTransform } from "./renderEntities";
import type { AssetCache } from "./assetCache";

function filterFor(e: ImageEntity): string {
  const parts: string[] = [];
  if (e.grayscale) parts.push("grayscale(1)");
  if (Math.abs(e.brightness) > 1e-6) parts.push(`brightness(${(1 + e.brightness).toFixed(3)})`);
  if (Math.abs(e.contrast) > 1e-6) parts.push(`contrast(${(1 + e.contrast).toFixed(3)})`);
  return parts.length > 0 ? parts.join(" ") : "none";
}

export function renderImages(
  v: RenderView,
  doc: Doc,
  cache: AssetCache,
  skipIds: ReadonlySet<string>,
  dim = false // IMG-12: ×0.4 alpha while a draw tool is active
): void {
  applyWorldTransform(v);
  const { ctx } = v;
  // descends into groups: images grouped with line-work must still render (INV-3)
  const visit = (id: string, layer: Doc["layers"][number]): void => {
    if (skipIds.has(id)) return;
    const entity = doc.entities[id];
    if (!entity || entity.hidden) return;
    if (entity.type === "group") {
      for (const childId of entity.children) visit(childId, layer);
    } else if (entity.type === "image") {
      drawImageEntity(v, dim ? { ...entity, opacity: entity.opacity * 0.4 } : entity, layer, cache);
    }
  };
  for (const layer of doc.layers) {
    if (!layer.visible) continue;
    for (const id of doc.zOrder[layer.id] ?? []) visit(id, layer);
  }
  ctx.filter = "none";
  ctx.globalAlpha = 1;
}

export function drawImageEntity(
  v: RenderView,
  e: ImageEntity,
  layer: Layer | undefined,
  cache: AssetCache
): void {
  const { ctx } = v;
  ctx.save();
  const cx = e.pos.x + e.wMm / 2;
  const cy = e.pos.y + e.hMm / 2;
  ctx.translate(cx, cy);
  if (Math.abs(e.rotationDeg) > 1e-9) ctx.rotate((e.rotationDeg * Math.PI) / 180);
  ctx.translate(-e.wMm / 2, -e.hMm / 2);
  ctx.globalAlpha = e.opacity * (layer?.opacity ?? 1);

  const cached =
    e.homography && e.rectifiedW && e.rectifiedH
      ? cache.getRectified(e.assetHash, e.homography, e.rectifiedW, e.rectifiedH)
      : cache.get(e.assetHash);

  if (!cached) {
    // loading, or missing asset (EC-EXP-5): gray placeholder
    ctx.fillStyle = cache.isMissing(e.assetHash) ? "#3a3f4c" : "#262b36";
    ctx.fillRect(0, 0, e.wMm, e.hMm);
    ctx.restore();
    return;
  }
  ctx.filter = filterFor(e);
  // IMG-4 mip selection: on-screen pixels needed across the placed width
  const bitmap = cache.pick(cached, e.wMm * v.cam.scale * v.dpr);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  // IMG-6 crop: normalized source sub-rect fills the placed box
  const c = e.crop;
  const sx = c ? c.x * bitmap.width : 0;
  const sy = c ? c.y * bitmap.height : 0;
  const sw = c ? c.w * bitmap.width : bitmap.width;
  const sh = c ? c.h * bitmap.height : bitmap.height;
  ctx.drawImage(bitmap, sx, sy, sw, sh, 0, 0, e.wMm, e.hMm);
  ctx.filter = "none";
  ctx.restore();
}
